import { createFormDataRequest } from "@/helpers/FormHelpers";
import { IPaginateResponse } from "@/interfaces/Fetch";
import {
  default as IUser,
  default as User,
  UserFilters,
  UserFormSend,
  UserUpdateFormSend,
} from "@/interfaces/IUser";
import api from "../api";
import { setCredentials } from "../slices/authSlice";

export const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<IPaginateResponse<IUser>, UserFilters>({
      query: (data) => {
        const params = createFormDataRequest(data);
        return {
          url: "/users",
          params,
        };
      },
      providesTags: (result, _error, _arg) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Users" as const, id })),
              { type: "Users", id: "LIST" },
            ]
          : [],
    }),
    storeUser: build.mutation<IUser, UserFormSend>({
      query: (data) => {
        const body = createFormDataRequest(data);
        return {
          url: "/users",
          method: "POST",
          body,
        };
      },
      invalidatesTags: (_result, _err, _body) => [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Users", id: "LIST" },
      ],
    }),
    updateUser: build.mutation<IUser, [string, UserUpdateFormSend]>({
      query: ([id, data]) => {
        const body = createFormDataRequest(data);
        return {
          url: `/users/${id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (_user, _err, [id]) => [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Users", id },
      ],
    }),
    getUser: build.query<IUser, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_user, _err, id) => [{ type: "Users", id }],
    }),
    destroyUser: build.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "CommercialBooking", id: "LIST" },
        { type: "Users", id: "LIST" },
      ],
    }),
    getCalendarFilterable: build.query<
      IUser[],
      { query?: string; roles?: string[] } | void
    >({
      query: (data) => ({
        url: "/users/calendar-filterable",
        params: data || {},
      }),
      providesTags: [{ type: "Users", id: "CALENDAR_FILTERABLE" }],
    }),
    getMyProfile: build.query<User, string>({
      query: (token) => ({
        url: "/users/me",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      async onQueryStarted(token, { queryFulfilled, dispatch }) {
        const { data: user } = await queryFulfilled;
        dispatch(setCredentials({ user, token }));
      },
      providesTags: ["Me"],
    }),
    updateCursorsEnabled: build.mutation<User, boolean>({
      query: (cursorsEnabled) => ({
        url: "/users/me/cursors-enabled",
        method: "PATCH",
        body: { cursorsEnabled },
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        const { data: user } = await queryFulfilled;
        dispatch(setCredentials({ user }));
      },
      invalidatesTags: ["Me"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useLazyGetUsersQuery,
  useStoreUserMutation,
  useDestroyUserMutation,
  useLazyGetMyProfileQuery,
  useUpdateCursorsEnabledMutation,
  useGetCalendarFilterableQuery,
  useLazyGetCalendarFilterableQuery,
} = usersApi;
